import { useEffect, useState, useRef } from "react";
import { api, formatApiError, PRIORITIES } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Send, RotateCcw, Sparkles, ArrowRightLeft, PauseCircle, ShieldAlert } from "lucide-react";
import TaskContextPanel from "@/components/TaskContextPanel";
import { FreshlyActivatedIndicator } from "@/components/SleepingIndicators";
import { isFreshlyActivated } from "@/lib/taskLinks";
import ParticipantsSection from "@/components/ParticipantsSection";
import AddParticipantDialog from "@/components/AddParticipantDialog";
import DeleteWithDependentsDialog from "@/components/DeleteWithDependentsDialog";
import ForceActivateDialog from "@/components/ForceActivateDialog";
import ManualLinkPickerDialog from "@/components/ManualLinkPickerDialog";
import SubtasksSection from "@/components/SubtasksSection";
import TaskCommentsSection from "@/components/TaskCommentsSection";

const STATUS_OPTIONS = [
  { value: "open", label: "Otvoren" },
  { value: "in_progress", label: "U toku" },
  { value: "waiting", label: "Čeka" },
  { value: "blocked", label: "Blokiran" },
  { value: "done", label: "Završen" },
];

const WAITING_CATEGORIES = [
  { value: "odgovor", label: "Odgovor" },
  { value: "odluka", label: "Odluka" },
  { value: "odobrenje", label: "Odobrenje" },
  { value: "dokument", label: "Dokument" },
  { value: "ponuda", label: "Ponuda" },
  { value: "informacija", label: "Informacija" },
  { value: "isporuka", label: "Isporuka" },
  { value: "uplata", label: "Uplata" },
  { value: "ostalo", label: "Ostalo" },
];

function todayLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function TaskEditDialog({ open, onOpenChange, task, collaborators = [], onChanged, onDeleted, onSideEffect }) {
  const [form, setForm] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);
  const [context, setContext] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [contactsById, setContactsById] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [deleteState, setDeleteState] = useState({ open: false, dependents: [], dependentsMeta: {} });
  const [forceState, setForceState] = useState({ open: false, blockingTitles: [], attemptedStatus: null });
  const [pickerOpen, setPickerOpen] = useState(false);
  const originalPriority = useRef(null);
  const originalStatus = useRef(null);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        priority: task.priority || "srednji",
        due_date: task.due_date || "",
        status: task.status || "open",
        collaborator_id: task.collaborator_id || "",
        from_collaborator_id: task.from_collaborator_id || "",
        direction: task.direction || "to_me",
        waiting: task.waiting || null,
        blocked: task.blocked || null,
      });
      setNotes(task.notes || []);
      setParticipants(task.participants || []);
      originalPriority.current = task.priority;
      originalStatus.current = task.status || "open";
      setNoteText("");
      setContext(null);
      api.post(`/tasks/${task.id}/read`).catch(() => {});
      api.get(`/tasks/${task.id}/context`)
        .then(({ data }) => setContext(data))
        .catch(() => setContext(null));
      // Fetch contacts for participant lookup (used in ParticipantsSection).
      // We fetch all contacts for now — cheap on typical org sizes.
      const ids = (task.participants || []).map((p) => p.contact_id);
      // Always fetch contacts — SubtasksSection also needs internal_users for assignee picker
      api.get("/contacts").then(({ data }) => {
        const map = {};
        for (const c of data || []) map[c.id] = c;
        setContactsById(map);
      }).catch(() => setContactsById({}));
    }
  }, [task]);

  const persistParticipants = async (next) => {
    setParticipants(next);
    try {
      await api.patch(`/tasks/${task.id}`, { participants: next });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addParticipant = async ({ contact, role }) => {
    if (!contact) return;
    // De-dupe by (contact_id, role)
    if (participants.some((p) => p.contact_id === contact.id && p.role === role)) {
      toast.info("Već je učesnik u toj ulozi");
      return;
    }
    setContactsById((m) => ({ ...m, [contact.id]: contact }));
    const next = [...participants, { contact_id: contact.id, role }];
    await persistParticipants(next);
  };

  const removeParticipant = async (row) => {
    const next = participants.filter(
      (p) => !(p.contact_id === row.contact_id && p.role === row.role)
    );
    await persistParticipants(next);
  };

  if (!task || !form) return null;
  const isCross = !!form.from_collaborator_id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When user picks a new status, fill sensible defaults for the sub-panel
  const setStatus = (v) => {
    setForm((f) => {
      const patch = { ...f, status: v };
      if (v === "waiting" && !f.waiting) {
        patch.waiting = { since_date: todayLocal(), category: "odgovor" };
      }
      if (v === "blocked" && !f.blocked) {
        patch.blocked = { started_at: todayLocal(), blocking_task_ids: [] };
      }
      return patch;
    });
  };
  const setW = (k, v) => setForm((f) => ({ ...f, waiting: { ...(f.waiting || {}), [k]: v } }));
  const setB = (k, v) => setForm((f) => ({ ...f, blocked: { ...(f.blocked || {}), [k]: v } }));

  const save = async () => {
    if (isCross && form.from_collaborator_id === form.collaborator_id) {
      return toast.error("'Od' i 'Za' ne smeju biti isti saradnik");
    }
    setSaving(true);
    try {
      const patch = {
        title: form.title,
        description: form.description,
        priority: form.priority,
        due_date: form.due_date || null,
        status: form.status,
        collaborator_id: form.collaborator_id,
        from_collaborator_id: form.from_collaborator_id || null,
        direction: isCross ? "to_them" : form.direction,
      };
      if (form.status === "waiting") patch.waiting = form.waiting || {};
      if (form.status === "blocked") patch.blocked = form.blocked || {};
      await api.patch(`/tasks/${task.id}`, patch);
      const rank = { nizak: 1, srednji: 2, visok: 3 };
      if (form.priority !== originalPriority.current && rank[form.priority] > rank[originalPriority.current || "srednji"]) {
        toast.success("Prioritet povećan — email obaveštenje poslato");
      } else {
        toast.success("Sačuvano");
      }
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      // 409 → uspavan task, ponudi Aktiviraj ipak
      if (e?.response?.status === 409 && e?.response?.data?.detail?.blocking_task_titles) {
        setForceState({
          open: true,
          blockingTitles: e.response.data.detail.blocking_task_titles || [],
          attemptedStatus: form.status,
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm("Obrisati task?")) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success("Obrisano");
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      if (e?.response?.status === 409 && e?.response?.data?.detail?.dependents) {
        const deps = e.response.data.detail.dependents;
        const meta = {};
        try {
          for (const d of deps) {
            const { data } = await api.get(`/tasks/${d.id}`);
            const others = (data.waiting_for_task_ids || []).filter((id) => id !== task.id);
            let pending = 0;
            for (const uid of others) {
              try {
                const { data: up } = await api.get(`/tasks/${uid}`);
                if (up.status !== "done") pending++;
              } catch {}
            }
            meta[d.id] = { pending_others: pending };
          }
        } catch {}
        setDeleteState({ open: true, dependents: deps, dependentsMeta: meta });
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  const toggleStatus = () => set("status", form.status === "done" ? "open" : "done");

  const addNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/tasks/${task.id}/notes`, { text: t });
      setNotes((prev) => [...prev, data]);
      setNoteText("");
      toast.success("Napomena dodata");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleCross = () => {
    if (isCross) set("from_collaborator_id", "");
    else {
      const other = collaborators.find((c) => c.id !== form.collaborator_id);
      set("from_collaborator_id", other?.id || "");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-edit-dialog" className="bg-white max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            Uredi task
            {task.ai_created && (
              <span className="inline-flex items-center gap-1 text-xs font-normal text-forest bg-surface-secondary border border-line px-2 py-0.5 rounded">
                <Sparkles className="w-3 h-3" /> AI
              </span>
            )}
            {isFreshlyActivated(task) && (
              <FreshlyActivatedIndicator testid="dialog-freshly-activated" />
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {context && (
            <TaskContextPanel
              context={context}
              onDismissReopenWarning={async () => {
                try {
                  await api.patch(`/tasks/${task.id}`, { clear_reopen_warning: true });
                  setContext((c) => c ? { ...c, reopen_warning: null } : c);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
            />
          )}

          <ParticipantsSection
            participants={participants}
            contactsById={contactsById}
            onAdd={() => setAddOpen(true)}
            onRemove={removeParticipant}
          />

          <SubtasksSection
            task={task}
            contacts={Object.values(contactsById)}
            onChanged={() => onChanged?.()}
            onCompleteMain={async () => {
              try {
                await api.patch(`/tasks/${task.id}`, { status: "done" });
                toast.success("Task završen");
                onChanged?.();
                onOpenChange(false);
              } catch (e) { toast.error(formatApiError(e)); }
            }}
          />

          <TaskCommentsSection taskId={task.id} contactsById={contactsById} onMutated={() => onSideEffect?.()} />

          <div><Label>Naslov</Label><Input data-testid="edit-title" value={form.title} onChange={(e) => set("title", e.target.value)} /></div>
          <div><Label>Opis</Label><Textarea data-testid="edit-description" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>

          {isCross ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Od (saradnik)</Label>
                <Select value={form.from_collaborator_id} onValueChange={(v) => set("from_collaborator_id", v)}>
                  <SelectTrigger data-testid="edit-from" className="bg-white"><SelectValue placeholder="Izaberi" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Za (saradnik)</Label>
                <Select value={form.collaborator_id} onValueChange={(v) => set("collaborator_id", v)}>
                  <SelectTrigger data-testid="edit-to" className="bg-white"><SelectValue placeholder="Izaberi" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Saradnik</Label>
                <Select value={form.collaborator_id} onValueChange={(v) => set("collaborator_id", v)}>
                  <SelectTrigger data-testid="edit-collab" className="bg-white"><SelectValue placeholder="Izaberi" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Smer</Label>
                <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
                  <SelectTrigger data-testid="edit-direction" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="to_me">On duguje meni</SelectItem>
                    <SelectItem value="to_them">Ja dugujem njemu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <button type="button" onClick={toggleCross} data-testid="toggle-cross" className="text-xs text-forest hover:underline inline-flex items-center gap-1.5">
            <ArrowRightLeft className="w-3.5 h-3.5" />
            {isCross ? "Prebaci na: ja ↔ saradnik" : "Prebaci na: saradnik → saradnik"}
          </button>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Prioritet</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="edit-priority" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Rok</Label>
              <Input data-testid="edit-due" type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={setStatus}>
                <SelectTrigger data-testid="edit-status" className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Waiting sub-panel */}
          {form.status === "waiting" && (
            <div className="rounded-md border border-line bg-surface-secondary p-3 space-y-3" data-testid="waiting-panel">
              <div className="flex items-center gap-2 text-forest text-sm font-medium">
                <PauseCircle className="w-4 h-4" /> Detalji čekanja
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Čeka se od (saradnik)</Label>
                  <Select
                    value={form.waiting?.waiting_on_collaborator_id || "none"}
                    onValueChange={(v) => setW("waiting_on_collaborator_id", v === "none" ? null : v)}
                  >
                    <SelectTrigger data-testid="waiting-on-collab" className="bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="none">— nije saradnik —</SelectItem>
                      {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>...ili osoba/kompanija (slobodan tekst)</Label>
                  <Input
                    data-testid="waiting-on-external"
                    placeholder="npr. Dobavljač XY"
                    value={form.waiting?.waiting_on_external || ""}
                    onChange={(e) => setW("waiting_on_external", e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Šta se čeka</Label>
                <Input
                  data-testid="waiting-what"
                  placeholder="npr. potpisan ugovor"
                  value={form.waiting?.what || ""}
                  onChange={(e) => setW("what", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Od kada</Label>
                  <Input
                    data-testid="waiting-since"
                    type="date"
                    value={form.waiting?.since_date || ""}
                    onChange={(e) => setW("since_date", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Očekivano</Label>
                  <Input
                    data-testid="waiting-expected"
                    type="date"
                    value={form.waiting?.expected_date || ""}
                    onChange={(e) => setW("expected_date", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Kategorija</Label>
                  <Select
                    value={form.waiting?.category || "ostalo"}
                    onValueChange={(v) => setW("category", v)}
                  >
                    <SelectTrigger data-testid="waiting-category" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {WAITING_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.waiting?.reminder_count > 0 && (
                <div className="text-xs text-ink-muted">
                  Podsetnika: {form.waiting.reminder_count}
                  {form.waiting.last_reminded_at && <> · poslednji: {new Date(form.waiting.last_reminded_at).toLocaleString("sr-RS")}</>}
                  {form.waiting.last_reminder_note && <div className="italic mt-0.5">„{form.waiting.last_reminder_note}"</div>}
                </div>
              )}
            </div>
          )}

          {/* Blocked sub-panel */}
          {form.status === "blocked" && (
            <div className="rounded-md border border-line bg-surface-secondary p-3 space-y-3" data-testid="blocked-panel">
              <div className="flex items-center gap-2 text-terracotta text-sm font-medium">
                <ShieldAlert className="w-4 h-4" /> Detalji blokade
              </div>
              <div>
                <Label>Razlog blokade</Label>
                <Textarea
                  data-testid="blocked-reason"
                  rows={2}
                  value={form.blocked?.reason || ""}
                  onChange={(e) => setB("reason", e.target.value)}
                  placeholder="Kratko zašto ne može dalje"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Zavisi od saradnika</Label>
                  <Select
                    value={form.blocked?.depends_on_collaborator_id || "none"}
                    onValueChange={(v) => setB("depends_on_collaborator_id", v === "none" ? null : v)}
                  >
                    <SelectTrigger data-testid="blocked-on-collab" className="bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="none">— nije saradnik —</SelectItem>
                      {collaborators.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>...ili osoba/kompanija</Label>
                  <Input
                    data-testid="blocked-on-external"
                    value={form.blocked?.depends_on_external || ""}
                    onChange={(e) => setB("depends_on_external", e.target.value)}
                    placeholder="npr. Poreska uprava"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Blokada počela</Label>
                  <Input
                    data-testid="blocked-started"
                    type="date"
                    value={form.blocked?.started_at || ""}
                    onChange={(e) => setB("started_at", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Očekivano rešenje</Label>
                  <Input
                    data-testid="blocked-expected"
                    type="date"
                    value={form.blocked?.expected_resolution_date || ""}
                    onChange={(e) => setB("expected_resolution_date", e.target.value)}
                  />
                </div>
              </div>
              <div className="text-xs text-ink-muted">
                Povezivanje sa blokirajućim taskovima dolazi u Fazi 2B.
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleStatus}
              data-testid="edit-toggle-status"
              className={`btn-ghost !py-1.5 ${form.status === "done" ? "!text-forest" : ""}`}
            >
              {form.status === "done" ? <><RotateCcw className="w-4 h-4" /> Ponovo otvori</> : "Označi kao završeno"}
            </button>
          </div>

          {/* Notes */}
          <div className="pt-2 border-t border-line">
            <Label className="overline">Napomene</Label>
            <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
              {notes.length === 0 && <div className="text-xs text-ink-muted">Još nema napomena.</div>}
              {notes.map((n) => (
                <div key={n.id} className="text-sm bg-surface-secondary border border-line rounded-md p-2">
                  <div className="text-xs text-ink-muted">{new Date(n.at).toLocaleString("sr-RS")}</div>
                  <div>{n.text}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <Input
                data-testid="edit-note-input"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); }}}
                placeholder="Dodaj napomenu i pritisni Enter"
              />
              <button className="btn-primary !px-3" data-testid="edit-note-add" onClick={addNote}><Send className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="pt-3 border-t border-line/50">
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              data-testid="manual-link-picker-open"
              className="text-xs text-ink-muted hover:text-forest inline-flex items-center gap-1"
            >
              + Poveži sa postojećim zadatkom
            </button>
          </div>
        </div>
        <DialogFooter className="flex justify-between">
          <button className="btn-ghost !text-terracotta" onClick={del} data-testid="edit-delete-btn"><Trash2 className="w-4 h-4" /> Obriši</button>
          <div className="flex gap-2">
            <button className="btn-ghost" onClick={() => onOpenChange(false)}>Otkaži</button>
            <button className="btn-primary" disabled={saving} onClick={save} data-testid="edit-save-btn">{saving ? "Čuvam..." : "Sačuvaj"}</button>
          </div>
        </DialogFooter>
      </DialogContent>

      <AddParticipantDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onPicked={addParticipant}
        excludeContactIds={participants.map((p) => p.contact_id)}
      />

      <DeleteWithDependentsDialog
        open={deleteState.open}
        onOpenChange={(v) => setDeleteState((s) => ({ ...s, open: v }))}
        taskId={task?.id}
        taskTitle={task?.title}
        dependents={deleteState.dependents}
        dependentsMeta={deleteState.dependentsMeta}
        onDeleted={() => { onDeleted?.(); onOpenChange(false); }}
      />

      <ForceActivateDialog
        open={forceState.open}
        onOpenChange={(v) => setForceState((s) => ({ ...s, open: v }))}
        taskId={task?.id}
        taskTitle={task?.title}
        blockingTitles={forceState.blockingTitles}
        attemptedStatus={forceState.attemptedStatus}
        onActivated={() => { onChanged?.(); onOpenChange(false); }}
      />

      <ManualLinkPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        currentTask={task}
        onLinked={() => {
          // Reload context after link
          api.get(`/tasks/${task.id}/context`).then(({ data }) => setContext(data)).catch(() => {});
          onChanged?.();
        }}
      />
    </Dialog>
  );
}
